import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Case, Else, If, Switch, Then, When } from 'react-if'
import { ButtonVariant } from '@/atoms/Button'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { RadioButtonIcon } from '@/atoms/Icons/RadioButtonIcon'
import { RadioButtonUncheckedIcon } from '@/atoms/Icons/RadioButtonUncheckedIcon'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD, CaptionSM, CaptionXS, LabelSM, TitleSM } from '@/atoms/Text'
import { GuildOffer } from '@/constants/guild'
import { BoundedImage } from '@/molecules/BoundedImage'
import { GuildSignupButton } from '@/molecules/GuildSignupButton'
import { useGuildMember } from '@/services/GuildUserService'
import { formatPrice } from '@/services/PriceFormatter'
import { formatPromotionPriceDisplay, ValidatedPromotion } from '@/services/SaleService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { OmniGuildPricingLabel } from '@/views/GuildJoinView/OmniGuildPlanSelectionCard/OmniGuildPlanBox/OmniGuildPricingLabel'
import { OmniPricePerInterval } from '@/views/GuildJoinView/OmniGuildPlanSelectionCard/OmniGuildPlanBox/OmniGuildPricingLabel/OmniGuildPricingLabel'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

export interface GuildPlanBoxProps {
  className?: string
  offers: GuildOffer[]
  orderClass?: string
  disclosePriceInUSD?: boolean
  defaultSelectedOffer?: OfferTier
  promotedInterval?: 'monthly' | 'annual'
}

export type OfferTier = 'basic' | 'basic-no-ads' | 'premium'

export const OmniGuildPlanBox: React.FC<GuildPlanBoxProps> = ({
  className,
  offers,
  disclosePriceInUSD = false,
  defaultSelectedOffer = 'premium',
  promotedInterval,
}) => {
  const { t } = useTranslate('guild')
  const { isGuildMember } = useGuildMember()
  const { sale } = useGuildSignupContext()
  const [selectedOffer, setSelectedOffer] = useState<OfferTier>(defaultSelectedOffer)

  // Update selected offer when defaultSelectedOffer prop changes
  useEffect(() => {
    setSelectedOffer(defaultSelectedOffer)
  }, [defaultSelectedOffer])

  const offer = offers.find((o) => o.tier === selectedOffer) || offers[0]
  const yearlyPromotion = sale?.promotions.find((p) => p.offer.id === offer?.annual.bimOfferId)
  const monthlyPromotion = sale?.promotions.find((p) => p.offer.id === offer?.monthly.bimOfferId)

  const SELECTED_BG_CLASS_MAP = {
    basic: 'border-2 border-oxide bg-core-gray-100',
    'basic-no-ads': 'border-2 border-oxide bg-core-gray-100',
    premium: 'border-2 border-oxide bg-core-gray-100',
  }

  const orderedOffers = offers.sort((a, b) => a.monthly.standardPrice - b.monthly.standardPrice)

  const { locale } = useLocale()

  if (!offer) {
    return null
  }

  const getMonthlyPrice = (offer: GuildOffer) => {
    const offerId = offer.monthly.bimOfferId
    const promotion = sale?.promotions.find((p) => p.offer.id === offerId) as ValidatedPromotion
    const isPromoApplicable = Boolean(promotion)

    if (isPromoApplicable) {
      return formatPromotionPriceDisplay(promotion, locale)
    }
    return formatPrice(offer.monthly.standardPrice, { locale, includeDecimals: false, currency: 'USD' })
  }

  return (
    <div className={classNames(className, 'flex justify-center items-center w-full px-1.5 se:px-1 md:px-3')}>
      <div className="relative h-full max-w-[520px] flex-col overflow-hidden rounded-2xl bg-white p-2 text-left text-core-gray-950 md:flex md:p-4 md:px-3 se:px-1">
        <CaptionXS weight="bold">Choose your plan</CaptionXS>
        <div className="relative mt-2 flex w-full items-center justify-between gap-2">
          <div className="grid w-full grid-cols-3 gap-1">
            {orderedOffers.map((off, id) => (
              <div key={`${off.tier}-${id}`} className="relative col-span-1 h-[120px] rounded-lg">
                <When condition={off.tier === 'premium'}>
                  <span
                    className={classNames(
                      'absolute left-1/2 w-[80%] -translate-x-1/2 top-[-20px] h-5 rounded-t-md bg-[#DFAF89] pt-1 text-center text-[10px] font-bold',
                      off.tier === selectedOffer ? 'border-b border-oxide' : '',
                    )}
                  >
                    {t('mostPopular', 'Most Popular')}
                  </span>
                </When>
                <button
                  className={classNames(
                    'flex flex-col items-start justify-between rounded-lg border p-2 md:p-4 md:pt-2 text-start w-full h-full outline-1 outline-transparent',
                    off.tier === selectedOffer
                      ? SELECTED_BG_CLASS_MAP[off.tier]
                      : 'border-2 border-core-gray-300 bg-core-gray-100',
                  )}
                  onClick={() => setSelectedOffer(off.tier)}
                >
                  <Switch>
                    <Case condition={off.tier === 'basic'}>
                      <div>
                        <CaptionSM className="md:photon-paragraph-md" color="black" weight="bold">
                          {t('basicWithAds', 'Basic with Ads')}
                        </CaptionSM>
                        <CaptionXS color="core-gray-800">{t('streamIn1080p', 'Stream in 1080p')}</CaptionXS>
                      </div>
                      <CaptionXS color="core-gray-800">
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                    <Case condition={off.tier === 'basic-no-ads'}>
                      <div>
                        <CaptionSM className="md:photon-paragraph-md" color="black" weight="bold">
                          {t('basic', 'Basic')}
                        </CaptionSM>
                        <CaptionXS color="core-gray-800">{t('streamIn1080p', 'Stream in 1080p')}</CaptionXS>
                      </div>
                      <CaptionXS color="core-gray-800">
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                    <Case condition={off.tier === 'premium'}>
                      <div>
                        <CaptionSM className="md:photon-paragraph-md" color="black" weight="bold">
                          {t('premium', 'Premium')}
                        </CaptionSM>
                        <CaptionXS color="core-gray-800">
                          {t('streamIn4KFreeMovieTicketsAndMore', 'Stream in 4K, Free Movie Tickets & More')}
                        </CaptionXS>
                      </div>
                      <CaptionXS color="core-gray-800">
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                  </Switch>
                  <div className="absolute right-1 top-2 md:hidden">
                    {off.tier === selectedOffer ? (
                      <RadioButtonIcon size={12} color="oxide" className="overflow-visible" />
                    ) : (
                      <RadioButtonUncheckedIcon size={12} color="black" className="overflow-visible" />
                    )}
                  </div>
                  <div className="absolute right-2 top-2 hidden md:top-3.5 md:block">
                    {off.tier === selectedOffer ? (
                      <RadioButtonIcon size={16} color="oxide" className="overflow-visible" />
                    ) : (
                      <RadioButtonUncheckedIcon size={16} color="black" className="overflow-visible" />
                    )}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 pb-4 pt-6">
          <ul className={classNames('flex flex-col gap-2')}>
            <When condition={offer.tier === 'basic'}>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="shareFreeAccessShowsFilms">
                    Share free access to shows and films with friends and family
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="limitedAds">
                    Limited Ads
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="streamInFullHd">
                    Stream in 1080p (Full HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
            </When>
            <When condition={offer.tier === 'basic-no-ads'}>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="shareFreeAccessShowsFilms">
                    Share free access to shows and films with friends and family
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="adFreeHyphenated">
                    Ad-Free
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="streamInFullHd">
                    Stream in 1080p (Full HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
            </When>
            <When condition={offer.tier === 'premium'}>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="shareFreeAccessShowsFilms">
                    Share free access to shows and films with friends and family
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="adFreeHyphenated">
                    Ad-Free
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="streamIn4kUltraHd">
                    Stream in 4K (Ultra HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="twoTicketsToEveryReleasev5">
                    Two tickets to every Angel movie in theaters to use or give away
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="discountOnMerchV3">
                    Save 20% on merch in our online store
                  </Translate>
                </CaptionMD>
              </li>
            </When>
          </ul>
        </div>
        <div className="mt-auto flex flex-col gap-2 pb-5 pt-4">
          <PlanSelectorWell
            className="order-1"
            offer={offer}
            currency="USD"
            promotion={monthlyPromotion}
            planInterval="monthly"
            isGuildMember={isGuildMember}
            punchyButtonColors
            disclosePriceInUSD={disclosePriceInUSD}
            promotedInterval={promotedInterval}
          />
          <PlanSelectorWell
            className="order-0"
            currency="USD"
            offer={offer}
            promotion={yearlyPromotion}
            planInterval="annual"
            isGuildMember={isGuildMember}
            punchyButtonColors
            disclosePriceInUSD={disclosePriceInUSD}
            promotedInterval={promotedInterval}
          />
        </div>
      </div>
    </div>
  )
}

interface PlanSelectorWellProps {
  currency: string
  promotion?: ValidatedPromotion
  isGuildMember?: boolean
  planInterval: 'monthly' | 'annual'
  punchyButtonColors?: boolean
  disclosePriceInUSD?: boolean
  className?: string
  offer: GuildOffer
  promotedInterval?: 'monthly' | 'annual'
}

function useButtonStyles({
  planInterval,
  promotedInterval = 'annual',
}: {
  planInterval: 'monthly' | 'annual'
  promotedInterval?: 'monthly' | 'annual'
}) {
  const buttonClassName = ''

  const buttonVariant: ButtonVariant = planInterval === promotedInterval ? 'black' : 'plain'

  return { buttonClassName, buttonVariant }
}

export const PlanSelectorWell: React.FC<PlanSelectorWellProps> = ({
  currency,
  promotion,
  planInterval,
  isGuildMember,
  disclosePriceInUSD,
  className,
  offer,
  promotedInterval,
}) => {
  const { t } = useTranslate('guild')
  const { slug } = useGuildSignupContext()
  const router = useSafeRouter()

  // This component usually loads below the fold, so we only load images when it's in view.
  // On the guild pricing page, we want to load images eagerly because this component is above the fold on that page.
  const isGuildPricingPage = router.pathname.endsWith('/guild/pricing')

  const shouldShowExtraTickets = planInterval === 'annual'

  const { buttonClassName, buttonVariant } = useButtonStyles({
    planInterval,
    promotedInterval,
  })
  const canShowTMobileLogo = planInterval === 'monthly' && offer.tier === 'premium' && slug === 'tmtdec25'

  return (
    <div
      className={classNames(
        'relative rounded-lg p-4 bg-core-gray-100 border border-core-gray-300',
        { '!border-[#E20074] !border-2': canShowTMobileLogo },
        className,
      )}
    >
      <div className="flex">
        <div className="grow-1">
          <If condition={planInterval === 'annual'}>
            <Then>
              <CaptionLG weight="semibold" className="uppercase">
                <Translate i18nKey="annual" t={t}>
                  Annual
                </Translate>
              </CaptionLG>
            </Then>
            <Else>
              <CaptionLG weight="semibold" className="uppercase">
                <Translate i18nKey="monthly" t={t}>
                  Monthly
                </Translate>
              </CaptionLG>
            </Else>
          </If>
          <OmniPricePerInterval
            className="mb-2"
            planInterval={planInterval}
            disclosePriceInUSD={disclosePriceInUSD}
            offer={offer}
          />
          <div className="-mb-3">
            <OmniGuildPricingLabel currency={currency} planInterval={planInterval} offer={offer} />
          </div>
        </div>
        <div className="ml-auto w-fit grow-0">
          <GuildSignupButton
            currency={currency}
            offerId={offer[planInterval].bimOfferId}
            price={offer[planInterval].standardPrice}
            promoCode={promotion ? promotion?.promotionCode.code : null}
            planInterval={planInterval}
            className={classNames('ml-auto !w-fit', buttonClassName)}
            buttonVariant={buttonVariant}
          >
            <LabelSM className="capitalize">
              <If condition={isGuildMember}>
                <Then>
                  <Translate t={t} i18nKey="upgradeMembership">
                    Upgrade Membership
                  </Translate>
                </Then>
                <Else>
                  <Translate t={t} i18nKey="select">
                    Select
                  </Translate>
                </Else>
              </If>
            </LabelSM>
          </GuildSignupButton>
          {canShowTMobileLogo && (
            <div className="ml-auto mt-2 h-[40px] w-[63px]">
              <BoundedImage
                className="flex h-full w-full rounded-xl"
                alt={t('tMobileLogo', 'T-mobile logo')}
                path={'https://images.angelstudios.com/image/upload/v1765302394/angel-studios/misc-icons/TMT-logo.png'}
                width={63}
                height={40}
                sizes="63px"
                imageClassName="h-full w-full object-contain"
              />
            </div>
          )}
        </div>
      </div>
      <When condition={shouldShowExtraTickets}>
        <div className="-mb-4 pt-4">
          <div className="relative overflow-hidden border-t border-core-gray-500">
            <div className="flex">
              <div className="py-4 pr-4">
                <TitleSM color="black" weight="bold" className="mb-1">
                  <Translate t={t} i18nKey="plusTwoBonusTickets">
                    +2 Bonus Tickets
                  </Translate>

                  <If condition={offer.tier === 'premium'}>
                    <Then>
                      <CaptionSM weight="semibold" color="core-gray-800">
                        <Translate t={t} i18nKey="fourTotalWithPremium60Value">
                          4 Total with Premium, <span className="font-bold text-core-oxide">$60 value</span>
                        </Translate>
                      </CaptionSM>
                    </Then>
                    <Else>
                      <CaptionSM weight="semibold" color="core-gray-800">
                        <Translate t={t} i18nKey="30Value">
                          <span className="font-bold text-core-oxide">$30 value</span>
                        </Translate>
                      </CaptionSM>
                    </Else>
                  </If>
                </TitleSM>
                <CaptionMD color="core-gray-700">
                  <Translate t={t} i18nKey="limitedOfferBonusTicketsV3">
                    Special Offer: Get two extra tickets for <i>DAVID</i> in theaters!
                  </Translate>
                </CaptionMD>
              </div>
              <div className="max-h-[125px] w-1/2 content-end items-stretch overflow-hidden lg:w-2/3">
                <Image
                  alt="Two Bonus Tickets"
                  src="https://images.angelstudios.com/image/upload/v1763649314/tickets-david-full_dhbcbb.webp"
                  className="w-full object-cover"
                  width={450}
                  height={279}
                  loading={isGuildPricingPage ? 'eager' : 'lazy'}
                />
              </div>
            </div>
          </div>
        </div>
      </When>
    </div>
  )
}
